import { Link, Navigate, useLocation } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { DE_HUB, DE_CITIES, DE_UI, DE_RELATED_SERVICES } from "@/data/deLocalized";
import { FR_HUB, FR_CITIES, FR_UI, FR_RELATED_SERVICES } from "@/data/frLocalized";

/**
 * Shared template for German + French localized landing pages.
 * Handles the hub (with hreflang cluster) and city pages (self-lang only).
 */

const LOCALES = {
  de: {
    hub: DE_HUB,
    cities: DE_CITIES,
    ui: DE_UI,
    services: DE_RELATED_SERVICES,
    countryName: "Deutschland",
    hubUrlLabel: "Deutschland-Übersicht",
    citiesHeading: "Städte in Deutschland",
    faqHeading: "Häufig gestellte Fragen",
    contactPath: "/contact",
    lang: "de",
    ctaBackToHubText: "Zurück zur Deutschland-Übersicht",
  },
  fr: {
    hub: FR_HUB,
    cities: FR_CITIES,
    ui: FR_UI,
    services: FR_RELATED_SERVICES,
    countryName: "France",
    hubUrlLabel: "France",
    citiesHeading: "Villes en France",
    faqHeading: "Questions fréquentes",
    contactPath: "/contact",
    lang: "fr",
    ctaBackToHubText: "Retour à la France",
  },
};

function resolveLocale(pathname) {
  if (pathname.startsWith("/de/")) return LOCALES.de;
  if (pathname.startsWith("/fr/")) return LOCALES.fr;
  return null;
}

export const LocalizedRegion = () => {
  const location = useLocation();
  const locale = resolveLocale(location.pathname);
  const { open } = useStartProject();

  const isHub = locale ? location.pathname === locale.hub.slug : false;
  const page = locale
    ? isHub
      ? locale.hub
      : locale.cities.find((c) => c.slug === location.pathname)
    : null;

  // hreflang cluster: only for the hub (has EN equivalent).
  // City pages have no EN equivalent — self-ref only.
  const alternates = !locale
    ? null
    : isHub
      ? [
          { hreflang: "en", href: `${SITE_URL}${locale.hub.hreflangCluster.en}` },
          { hreflang: locale.lang, href: `${SITE_URL}${locale.hub.hreflangCluster[locale.lang]}` },
          { hreflang: "x-default", href: `${SITE_URL}${locale.hub.hreflangCluster.xDefault}` },
        ]
      : [{ hreflang: locale.lang, href: `${SITE_URL}${location.pathname}` }];

  const breadcrumbCrumbs = !locale
    ? []
    : isHub
      ? [
          { name: locale.ui.breadcrumbHome, path: "/" },
          { name: locale.ui.breadcrumbRegions, path: "/regions" },
          { name: locale.countryName, path: locale.hub.slug },
        ]
      : [
          { name: locale.ui.breadcrumbHome, path: "/" },
          { name: locale.ui.breadcrumbRegions, path: "/regions" },
          { name: locale.countryName, path: locale.hub.slug },
          { name: page ? page.city : "", path: page ? page.slug : "" },
        ];

  useSeo({
    title: page ? page.title : locale ? `${locale.countryName} — India sourcing` : "Regions",
    description: page ? page.metaDescription : undefined,
    path: page ? page.slug : locale ? locale.hub.slug : "/regions",
    type: "article",
    alternates,
    jsonLd: page && locale
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}${page.slug}#service`,
              name: page.h1,
              description: page.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: isHub
                ? { "@type": "Country", name: locale.countryName }
                : {
                    "@type": "City",
                    name: page.city,
                    containedInPlace: { "@type": "Country", name: locale.countryName },
                  },
              serviceType: `India sourcing · ${locale.countryName}`,
              url: `${SITE_URL}${page.slug}`,
              inLanguage: locale.lang,
              isPartOf: isHub
                ? undefined
                : {
                    "@type": "WebPage",
                    "@id": `${SITE_URL}${locale.hub.slug}`,
                    name: locale.hub.h1,
                  },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${page.slug}#faq`,
              inLanguage: locale.lang,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb(breadcrumbCrumbs),
          ],
        }
      : null,
    jsonLdId: "localized-region-jsonld",
  });

  if (!locale) return <Navigate to="/regions" replace />;
  if (!page) return <Navigate to={locale.hub.slug} replace />;

  return (
    <article data-testid="localized-region-page" lang={locale.lang}>
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="localized-crumb-home">
              {locale.ui.breadcrumbHome}
            </Link>{" "}
            ·{" "}
            <Link to="/regions" style={{ color: "inherit" }} data-testid="localized-crumb-regions">
              {locale.ui.breadcrumbRegions}
            </Link>{" "}
            ·{" "}
            {isHub ? (
              locale.countryName
            ) : (
              <>
                <Link
                  to={locale.hub.slug}
                  style={{ color: "inherit" }}
                  data-testid="localized-crumb-hub"
                >
                  {locale.countryName}
                </Link>{" "}
                · {page.city}
              </>
            )}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="localized-eyebrow">
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="localized-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="localized-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="localized-cta-primary"
          >
            {locale.ui.ctaPrimary}
          </button>
          {!isHub && (
            <Link
              to={locale.hub.slug}
              className="btn btn-ghost"
              data-testid="localized-cta-hub"
            >
              {locale.ctaBackToHubText}
            </Link>
          )}
        </Reveal>
      </header>

      {/* KEY FACTS */}
      <section
        className="wrap"
        data-testid="localized-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {(page.keyFacts || []).map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {(page.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`localized-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Related services */}
        <Reveal as="section" style={{ marginTop: "2.6rem" }} data-testid="localized-related-services">
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            {locale.ui.relatedServicesHeading}
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {locale.services.map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={`localized-service-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Cities cross-link (hub only) */}
        {isHub && page.relatedCities && page.relatedCities.length > 0 && (
          <Reveal as="section" style={{ marginTop: "2.6rem" }} data-testid="localized-related-cities">
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {locale.citiesHeading}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {page.relatedCities.map((c, i) => (
                <Link
                  key={i}
                  to={c.slug}
                  data-testid={`localized-city-${i}`}
                  style={{
                    display: "block",
                    padding: "1.2rem 1.3rem",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                    textDecoration: "none",
                    background: "var(--paper)",
                  }}
                >
                  <p
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: "1.05rem",
                      letterSpacing: "-.005em",
                      margin: 0,
                    }}
                  >
                    {c.name} →
                  </p>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="localized-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          {locale.faqHeading}
        </h2>
        {(page.faqs || []).map(([q, a], i) => (
          <details
            key={i}
            data-testid={`localized-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* Final CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="localized-enquiry"
      >
        <Reveal as="p" className="eyebrow">
          {locale.ui.finalCtaEyebrow}
        </Reveal>
        <Reveal
          as="div"
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="localized-cta-enquiry"
          >
            {locale.ui.ctaPrimary}
          </button>
          <Link
            to={locale.contactPath}
            className="btn btn-ghost"
            data-testid="localized-cta-contact"
          >
            {locale.ui.ctaSecondary}
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

export default LocalizedRegion;
